/**
 * ImagePreviewModal - Fullscreen image preview with navigation
 * Supports keyboard navigation (arrows, ESC) and thumbnail strip
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  IconButton,
  Typography,
  Chip,
  alpha,
  useTheme,
  Fade,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { ImagePreviewModalProps } from './types';

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  open,
  images,
  initialIndex = 0,
  onClose,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isAnimating, setIsAnimating] = useState(false);

  // Reset to initial index when modal opens
  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
    }
  }, [open, initialIndex]);

  const currentImage = images[currentIndex];
  const hasMultiple = images.length > 1;

  const goToNext = useCallback(() => {
    if (isAnimating || !hasMultiple) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev + 1) % images.length);
    setTimeout(() => setIsAnimating(false), 200);
  }, [images.length, hasMultiple, isAnimating]);

  const goToPrev = useCallback(() => {
    if (isAnimating || !hasMultiple) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    setTimeout(() => setIsAnimating(false), 200);
  }, [images.length, hasMultiple, isAnimating]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          goToPrev();
          break;
        case 'ArrowRight':
          goToNext();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose, goToNext, goToPrev]);

  if (!currentImage) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.95)',
          backgroundImage: 'none',
          maxWidth: '95vw',
          maxHeight: '95vh',
          m: isMobile ? 0 : 2,
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          minHeight: isMobile ? '100vh' : '80vh',
          minWidth: isMobile ? '100vw' : '60vw',
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: 'white',
            bgcolor: alpha(theme.palette.common.white, 0.1),
            zIndex: 10,
            '&:hover': {
              bgcolor: alpha(theme.palette.common.white, 0.2),
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Image Counter */}
        {hasMultiple && (
          <Chip
            label={`${currentIndex + 1} / ${images.length}`}
            size="small"
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              bgcolor: alpha(theme.palette.common.white, 0.15),
              color: 'white',
              fontWeight: 500,
              zIndex: 10,
            }}
          />
        )}

        {/* Main Image */}
        <Fade in key={currentIndex} timeout={200}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              width: '100%',
              p: { xs: 2, sm: 4 },
            }}
          >
            <Box
              component="img"
              src={currentImage.url}
              alt={`Image ${currentIndex + 1}`}
              sx={{
                maxWidth: '100%',
                maxHeight: hasMultiple ? 'calc(100vh - 200px)' : 'calc(100vh - 100px)',
                objectFit: 'contain',
                borderRadius: 1,
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              }}
            />
          </Box>
        </Fade>

        {/* Navigation Buttons */}
        {hasMultiple && (
          <>
            <IconButton
              onClick={goToPrev}
              disabled={isAnimating}
              sx={{
                position: 'absolute',
                left: { xs: 8, sm: 24 },
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                bgcolor: alpha(theme.palette.common.white, 0.1),
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 },
                '&:hover': {
                  bgcolor: alpha(theme.palette.common.white, 0.2),
                },
                '&:disabled': {
                  opacity: 0.3,
                },
              }}
            >
              <NavigateBeforeIcon sx={{ fontSize: { xs: 24, sm: 32 } }} />
            </IconButton>

            <IconButton
              onClick={goToNext}
              disabled={isAnimating}
              sx={{
                position: 'absolute',
                right: { xs: 8, sm: 24 },
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                bgcolor: alpha(theme.palette.common.white, 0.1),
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 },
                '&:hover': {
                  bgcolor: alpha(theme.palette.common.white, 0.2),
                },
                '&:disabled': {
                  opacity: 0.3,
                },
              }}
            >
              <NavigateNextIcon sx={{ fontSize: { xs: 24, sm: 32 } }} />
            </IconButton>
          </>
        )}

        {/* Thumbnail Strip */}
        {hasMultiple && !isMobile && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              p: 2,
              overflowX: 'auto',
              maxWidth: '100%',
              bgcolor: alpha(theme.palette.common.black, 0.5),
              borderRadius: 1,
              mb: 2,
              '&::-webkit-scrollbar': {
                height: 6,
              },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: alpha(theme.palette.common.white, 0.3),
                borderRadius: 3,
              },
            }}
          >
            {images.map((image, index) => (
              <Box
                key={image.id}
                onClick={() => !isAnimating && setCurrentIndex(index)}
                sx={{
                  width: 64,
                  height: 48,
                  flexShrink: 0,
                  borderRadius: 1,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: index === currentIndex
                    ? `2px solid ${theme.palette.primary.main}`
                    : '2px solid transparent',
                  opacity: index === currentIndex ? 1 : 0.6,
                  transition: 'all 0.2s',
                  '&:hover': {
                    opacity: 1,
                  },
                }}
              >
                <Box
                  component="img"
                  src={image.url}
                  alt={`Thumbnail ${index + 1}`}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            ))}
          </Box>
        )}

        {/* Keyboard hints */}
        {hasMultiple && !isMobile && (
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              bottom: 16,
              color: alpha(theme.palette.common.white, 0.5),
            }}
          >
            Use arrow keys to navigate, ESC to close
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImagePreviewModal;
